import React, {useRef, useState, useEffect} from "react"
import {Link} from "react-router-dom"
import moment from "moment"
import "./RescuePage.css"
import "./RescueOld.css"


const BODY_IMAGE = "https://public.gss.rs/body/body.old.png"


function Field({label, value}) {
    if (!value && value !== 0) return null
    return <div className="ro-field">
        <div className="ro-label">{label}</div>
        <div className="ro-value">{value}</div>
    </div>
}


function Tags({items}) {
    if (!items?.length) return null
    return <div className="ro-tags">
        {items.map(item => <span key={item} className="ro-tag">{item}</span>)}
    </div>
}


function BodyImage({points}) {
    const bodyRef = useRef()
    const [width, setWidth] = useState(0)

    useEffect(() => {
        const update = () => setWidth(bodyRef.current?.offsetWidth ?? 0)
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [])

    const getMarkerStyle = (point) => {
        const r = point.r ?? 0.025
        const markerSize = width * r
        if (markerSize <= 0) return {}
        return {
            left: `calc(${100 * point.x}% - ${markerSize / 2}px)`,
            top: `calc(${100 * point.y}% - ${markerSize / 2}px)`,
            width: `${markerSize}px`,
            height: `${markerSize}px`
        }
    }

    return <div className="ro-body" ref={bodyRef}>
        {points.map((point, i) =>
            <div key={i} className="ro-body-marker" style={getMarkerStyle(point)}
                 title={point.comment || ''}/>
        )}
        <img src={BODY_IMAGE} alt="" onLoad={() => setWidth(bodyRef.current?.offsetWidth ?? 0)}/>
    </div>
}


export default function RescueOld({rescue}) {

    const r = rescue.rescue
    const subject = r.subject ?? {}
    const events = (r.events ?? []).sort((a, b) => a.time < b.time ? -1 : 1)
    const location = r.location ?? {}
    const rescuers = r.rescuers ?? []
    const fields = r.fields ?? []

    const hasResort = r.ski_pass || r.accommodation || r.subject_category || r.helmet

    const formatGender = (g) => {
        if (g === "male") return "Muško"
        if (g === "female") return "Žensko"
        return g
    }

    const formatDate = (d) => {
        if (!d) return null
        return moment(d).format("D.M.YYYY.")
    }

    return <section className="white shadow" id="rescue-old">
        <h1>
            <span>Povredna lista</span>
            <span>{r.id} &#x2022; {rescue.getSubjectName()}</span>
        </h1>

        {/* Subject */}
        <div className="ro-section">
            <h3>Podaci o povređenom</h3>
            <Field label="Ime" value={subject.given_name}/>
            <Field label="Prezime" value={subject.family_name}/>
            <Field label="Pol" value={formatGender(subject.gender)}/>
            <Field label="Datum rođenja" value={formatDate(subject.birth_date)}/>
            <Field label="Mesto rođenja" value={subject.birth_place}/>
            <Field label="Adresa" value={subject.address}/>
            <Field label="Telefon" value={subject.phone_number}/>
        </div>

        {/* Resort */}
        {hasResort && <div className="ro-section">
            <h3>Podaci za skijalište</h3>
            <Field label="Ski karta" value={r.ski_pass}/>
            <Field label="Smeštaj" value={r.accommodation}/>
            <Field label="Kategorija" value={r.subject_category}/>
            <Field label="Kaciga" value={r.helmet ? "Da" : "Ne"}/>
        </div>}

        {/* Injuries */}
        {r.injuries && <div className="ro-section">
            <h3>Povrede</h3>
            {r.injuries.types?.length > 0 && <>
                <div className="ro-label">Vrsta povrede</div>
                <Tags items={r.injuries.types}/>
            </>}
            {r.injuries.body_parts?.length > 0 && <>
                <div className="ro-label">Povređeni delovi tela</div>
                <Tags items={r.injuries.body_parts}/>
            </>}
            {r.injuries.points?.length > 0 && <BodyImage points={r.injuries.points}/>}
        </div>}

        {/* Timeline */}
        {events.length > 0 && <div className="ro-section">
            <h3>Vreme</h3>
            <div className="ro-field">
                <div className="ro-label">Datum</div>
                <div className="ro-value">{formatDate(events[0]?.time)}</div>
            </div>
            <div className="ro-events">
                {events.map((event, i) => <div key={i} className="ro-event">
                    <span className="ro-event-time">{moment(event.time).format("HH:mm")}</span>
                    <span className="ro-event-name">{event.event}</span>
                </div>)}
            </div>
        </div>}

        {/* Location */}
        {(location.area || location.piste || location.description) && <div className="ro-section">
            <h3>Lokacija</h3>
            <Field label="Područje" value={r.area}/>
            <Field label="Staza" value={location.piste?.name}/>
            {location.off_piste && <Field label="Van staze" value="Da"/>}
            <Field label="Opis lokacije" value={location.description}/>
        </div>}

        {/* Weather */}
        {(r.weather?.length > 0 || r.weather_desc) && <div className="ro-section">
            <h3>Meteorološki uslovi</h3>
            <Tags items={r.weather}/>
            {r.weather_desc && <Field label="Ostalo" value={r.weather_desc}/>}
        </div>}

        {/* Aid & Transport */}
        {(r.treatment?.length > 0 || r.treatment_desc || r.transport?.length > 0 || r.transport_desc) && <div className="ro-section">
            <h3>Pružena pomoć i transport</h3>
            {(r.treatment?.length > 0 || r.treatment_desc) && <>
                <div className="ro-label">Pružena pomoć</div>
                <Tags items={r.treatment}/>
                {r.treatment_desc && <Field label="Ostalo" value={r.treatment_desc}/>}
            </>}
            {(r.transport?.length > 0 || r.transport_desc) && <>
                <div className="ro-label" style={{marginTop: "0.5em"}}>Transport</div>
                <Tags items={r.transport}/>
                {r.transport_desc && <Field label="Ostalo" value={r.transport_desc}/>}
            </>}
        </div>}

        {/* End */}
        {(r.belongings || r.receiver || r.material_desc) && <div className="ro-section">
            <h3>Završetak akcije</h3>
            <Field label="Lična oprema predata" value={r.belongings}/>
            <Field label="Povređenog primio" value={r.receiver}/>
            <Field label="Medicinski materijal" value={r.material_desc}/>
        </div>}

        {/* Rescuers */}
        <div className="ro-section">
            <h3>Učesnici</h3>
            {r.rescuer && <div>
                <div className="ro-label">Vođa akcije</div>
                <div className="ro-rescuer">
                    <Link to={`/rescuer/${r.rescuer.username}`}>{r.rescuer.given_name} {r.rescuer.family_name}</Link>
                    {(r.rescuer.nickname || r.rescuer.gss_id) && <span className="ro-rescuer-detail">
                        {" "}({[r.rescuer.nickname, r.rescuer.gss_id].filter(Boolean).join(", ")})
                    </span>}
                </div>
            </div>}
            {rescuers.length > 0 && <div>
                <div className="ro-label" style={{marginTop: "0.5em"}}>Ostali učesnici</div>
                {rescuers.map((rescuer, i) => <div key={i} className="ro-rescuer">
                    <Link to={`/rescuer/${rescuer.username}`}>{rescuer.given_name} {rescuer.family_name}</Link>
                    {(rescuer.nickname || rescuer.gss_id) && <span className="ro-rescuer-detail">
                        {" "}({[rescuer.nickname, rescuer.gss_id].filter(Boolean).join(", ")})
                    </span>}
                </div>)}
            </div>}
        </div>

        {/* Additional fields */}
        {fields.length > 0 && <div className="ro-section">
            <h3>Dodatna polja</h3>
            {fields.map((field, i) => <div key={i} className="ro-field-pair">
                <span className="ro-pair-name">{field.name}:</span> {field.value}
            </div>)}
        </div>}

        {/* Description */}
        {r.description && <div className="ro-section">
            <h3>Opis</h3>
            <div className="ro-description">{r.description}</div>
        </div>}
    </section>
}
